import { createFileRoute } from '@tanstack/react-router';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import * as metricsApi from '@/api/metrics';
import { usePermissions } from '@/hooks/usePermissions';
import { LoadingScreen } from '@/components/LoadingScreen';

export const Route = createFileRoute('/_authenticated/dashboard/metrics')({
  component: MetricsPage,
});

const usdFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * Dashboard de métricas de negocio cross-tenant. Solo accesible para roles de plataforma
 * (Operator/SuperAdmin) — gateado por <c>canViewMetrics</c> en UI; el backend lo enforce con el
 * scope <c>platform-metrics:read</c>.
 */
function MetricsPage(): JSX.Element {
  const { canViewMetrics } = usePermissions();

  const metricsQ = useQuery({
    queryKey: ['metrics', 'business'],
    queryFn: metricsApi.getBusinessMetrics,
    enabled: canViewMetrics,
  });

  if (!canViewMetrics) {
    return (
      <Alert severity="warning" sx={{ m: 3 }} data-testid="metrics-forbidden">
        No tenés permiso para ver las métricas de plataforma. Requiere rol Operator o SuperAdmin.
      </Alert>
    );
  }

  if (metricsQ.isLoading) {
    return <LoadingScreen message="Cargando métricas…" />;
  }

  if (metricsQ.isError || !metricsQ.data) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        No pudimos cargar las métricas de negocio.
      </Alert>
    );
  }

  const m = metricsQ.data;
  const naLabel = 'no disponible';

  const funnel = [
    { name: 'Nuevos (30d)', value: m.newTenantsLast30Days },
    { name: 'Activados', value: m.activatedTenants ?? 0 },
    { name: 'Churn (30d)', value: m.churnedLast30Days },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Métricas de negocio
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        KPIs agregados de toda la plataforma (cross-tenant).
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi label="MRR" value={usdFormat.format(m.mrr)} hint="Suscripciones activas" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi label="Nuevos tenants (30d)" value={String(m.newTenantsLast30Days)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi label="Churn (30d)" value={String(m.churnedLast30Days)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi
            label="CAEs emitidos"
            value={
              m.invoiceMetricsAvailable && m.totalInvoicesIssued != null
                ? String(m.totalInvoicesIssued)
                : naLabel
            }
            hint={
              m.invoiceMetricsAvailable
                ? `${m.activatedTenants ?? 0} tenants activados`
                : 'Invoicing no disponible'
            }
          />
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Funnel de tenants
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnel}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#1976d2" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          {!m.invoiceMetricsAvailable ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Las métricas de facturas (CAEs, tenants activados) no están disponibles ahora — el
              servicio Invoicing no respondió.
            </Alert>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}
